"use client";
import { Link } from "@/i18n/routing";
import { IconLinkArrow } from "@repo/assets/images";
import { CarouselButton, DotButton } from "@repo/components";
import { CartContext, useCartContext } from "@repo/contexts/CartContext";
import { useCarouselButtons, useDotButton } from "@repo/lib/hooks/common";
import { ProductCard } from "@repo/ui";
import cx from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { useLocale } from "next-intl";
import { useCallback } from "react";

const ProductSlider = (props) => {
  const {
    title,
    items,
    wishlistItems,
    className = "",
    isMedium,
    session,
    isMiniCartSlider,
    isDotButton = false,
    slug = "",
    type = "",
  } = props;

  const locale = useLocale();
  const isRTL = locale === "ar";
  const OPTIONS = {
    loop: true,
    align: "start",
    slidesToScroll: 1,
    direction: isRTL ? "rtl" : "ltr",
  };
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = useCarouselButtons(emblaApi);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const isCarouselButtonEnabled =
    items?.length > 4 || (isMiniCartSlider && items?.length > 1);
  const { setShowMiniCart } = useCartContext(CartContext);

  // Close minicart on click of wishlist item in minicart
  const handleCloseMiniCart = useCallback(
    () => setShowMiniCart(false),
    [setShowMiniCart]
  );

  const isItemCountLessThanFour = items?.length <= 4;
  const linkPathUrl =
    type === "favorite" ? `/account/favourites` : `/collections/${slug}`;

  return (
    <div
      className={cx({
        "py-4": isMedium || isMiniCartSlider,
      })}
    >
      {title && (
        <div className="mb-5 block flex flex-row justify-between font-normal md:mb-9 md:items-center">
          <span
            className={cx("font-heading text-primarygray rtl:font-arabic", {
              "text-2xl md:text-4xl": isMedium,
              "text-sm md:text-base": !isMedium,
            })}
          >
            {title}
          </span>
          <Link
            href={`${linkPathUrl}`}
            className="group inline-flex items-center transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
          >
            <IconLinkArrow className="display-featureArrowIcon text-icon-color size-6 fill-current transition-all duration-300 group-hover:translate-x-2 rtl:rotate-180 rtl:group-hover:-translate-x-2" />
          </Link>
        </div>
      )}
      <div className={`m-auto max-w-full ${className}`}>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="ml-[calc(1rem*-1)] flex touch-pan-y touch-pinch-zoom">
            {items?.map((item) => (
              <div
                className={cx("flex-[0_0_45%] pl-4", {
                  "md:flex-[0_0_25%]": isMedium,
                  "md:flex-[0_0_33.33%]": !isMedium,
                  "md:flex-[0_0_75%]": !isMedium && isMiniCartSlider,
                })}
                key={item?.id}
              >
                <div className="flex">
                  <ProductCard
                    product={item}
                    wishlistItems={wishlistItems}
                    session={session}
                    handleCloseMiniCart={handleCloseMiniCart}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className={cx("relative flex items-center justify-end", {
          "py-6": isMedium,
          "py-2.5": !isMedium,
        })}
      >
        {isDotButton && (
          <div
            className={cx(
              "absolute inset-0 flex items-center justify-center gap-2",
              { "md:hidden": isItemCountLessThanFour }
            )}
          >
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={cx("h-2 w-2 rounded-full", {
                  "bg-hero-dot-active": index === selectedIndex,
                  "bg-hero-dot-inactive": index !== selectedIndex,
                })}
                aria-label={index}
              />
            ))}
          </div>
        )}
        {isCarouselButtonEnabled && (
          <div className="hidden md:block">
            <div className="flex gap-6">
              <CarouselButton
                direction="prev"
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
                className="bg-icon-bg text-icon-color rounded-full"
              />
              <CarouselButton
                direction="next"
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
                className="bg-icon-bg text-icon-color rounded-full"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSlider;
